import { type ReviewItem } from "@/components/ReviewTable";
import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import { CounterOfferDialog } from "@/components/commerce/counter-offer-dialog";
import { QuoteReviewTable } from "@/components/commerce/quote-review-table";
import { QuoteSidebar } from "@/components/commerce/quote-sidebar";
import { QuoteSummaryStrip } from "@/components/commerce/quote-summary-strip";
import { DetailSkeleton } from "@/components/commerce/table-skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/lib/format";
import { useWorkspacePage } from "@/components/workspace";
import {
	useConvertQuoteToOrder,
	useGetQuote,
	useQuoteReviews,
	useUpdateQuote,
} from "@/hooks/use-commerce";
import { COMMERCE_KEYS } from "@/hooks/use-commerce";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { PipelineService } from "@/gen/omnios/v1/pipeline_connect";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowLeft,
	ArrowRight,
	Check,
	ClipboardPaste,
	ExternalLink,
	FileText,
	FileUp,
	Loader2,
	Save,
	X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const mediaClient = createClient(MediaService, transport);
const pipelineClient = createClient(PipelineService, transport);

const ACCEPTED_FILE_TYPES =
	".pdf,.xlsx,.xls,.docx,.doc,.csv,.txt,.jpg,.jpeg,.png";

export const Route = createFileRoute("/commerce/quotes/$id")({
	component: QuoteDetailPage,
});

function QuoteDetailPage() {
	const { t } = useTranslation("quotes");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const qc = useQueryClient();
	useEscapeBack("/commerce/quotes");

	const { data: quote, isLoading, error } = useGetQuote(id);
	const { data: reviewsData } = useQuoteReviews(id, !!quote);

	const convertMut = useConvertQuoteToOrder(id);
	const updateMut = useUpdateQuote(id);

	// Pipeline runs for this quote
	const { data: runsData } = useQuery({
		queryKey: ["entity-runs", "quote", id],
		queryFn: () =>
			pipelineClient.listEntityRuns({
				entityType: "quote",
				entityId: id,
			}),
		enabled: !!quote,
		refetchInterval: (query) => {
			// Poll while any run is in-progress
			const runs = query.state.data?.runs ?? [];
			const hasActive = runs.some(
				(r) => !r.completedAt && !r.stoppedAtStep,
			);
			return hasActive ? 3000 : false;
		},
	});
	const allRuns = runsData?.runs ?? [];
	const activeRuns = allRuns.filter((r) => !r.completedAt);
	const failedRuns = allRuns.filter((r) => r.stoppedAtStep);
	const lastFailedRun = failedRuns[0];
	const prevActiveRef = useRef(activeRuns.length);

	// When processing finishes, refresh reviews
	useEffect(() => {
		if (prevActiveRef.current > 0 && activeRuns.length === 0) {
			qc.invalidateQueries({ queryKey: COMMERCE_KEYS.quoteReviews(id) });
			qc.invalidateQueries({ queryKey: COMMERCE_KEYS.quote(id) });
		}
		prevActiveRef.current = activeRuns.length;
	}, [activeRuns.length, qc, id]);

	const [title, setTitle] = useState("");
	const [titleDirty, setTitleDirty] = useState(false);
	const [notes, setNotes] = useState("");
	const [counterOpen, setCounterOpen] = useState(false);
	const [processText, setProcessText] = useState("");
	const [processFile, setProcessFile] = useState<File | null>(null);
	const [dragOver, setDragOver] = useState(false);
	const [activeTab, setActiveTab] = useState("positions");
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (quote) {
			setTitle(quote.title || `КП-${id.slice(0, 8)}`);
			setNotes(quote.notes);
			setTitleDirty(false);
		}
	}, [quote, id]);

	useBreadcrumbs([
		{ label: t("title"), href: "/commerce/quotes" },
		{ label: quote?.title || `КП-${id.slice(0, 8)}` },
	]);

	const reviewItems: ReviewItem[] = useMemo(
		() =>
			(reviewsData?.reviews ?? []).map((r) => ({
				id: r.id,
				title: r.rawText,
				attrs: {
					toJson: () => ({
						review_status: r.status,
						raw_text: r.rawText,
						matched_product_id: r.matchedProductId,
						matched_product_title: r.matchedProductTitle,
						match_confidence: r.matchConfidence,
						parsed_quantity: r.parsedQuantity,
						parsed_unit: r.parsedUnit,
						selling_unit: r.sellingUnit,
						converted_quantity: r.convertedQuantity,
						unit_price_cents: Number(r.unitPriceCents),
						currency: r.currency,
						order_id: r.orderId,
						reject_reason: r.rejectReason,
						triage_path: (r.attrs?.toJson() as Record<string, unknown>)
							?.triage_path,
						rewritten_query: (r.attrs?.toJson() as Record<string, unknown>)
							?.rewritten_query,
						extracted_attributes: (
							r.attrs?.toJson() as Record<string, unknown>
						)?.extracted_attributes,
						suggested_category: (r.attrs?.toJson() as Record<string, unknown>)
							?.suggested_category,
					}),
				},
			})),
		[reviewsData],
	);

	const isDraft = quote?.status === "draft";
	const isNegotiating = quote?.status === "negotiating";
	const isAccepted = quote?.status === "accepted";
	const isConverted = quote?.status === "converted_to_order";
	const canEdit = isDraft || isNegotiating;
	const hasReviews = reviewItems.length > 0;
	const isEmpty = !hasReviews && (quote?.lines.length ?? 0) === 0;

	const [isSubmittingProcess, setIsSubmittingProcess] = useState(false);

	const handleProcess = async () => {
		setIsSubmittingProcess(true);
		try {
			let mediaId: string | undefined;

			if (processFile) {
				const buf = await processFile.arrayBuffer();
				const media = await mediaClient.uploadMedia({
					filename: processFile.name,
					mimeType: processFile.type || "application/octet-stream",
					data: new Uint8Array(buf),
					entityType: "quote",
					entityId: id,
				});
				mediaId = media.id;
			}

			// Fire-and-forget: start the stream but don't await it
			// Pipeline polling handles status updates
			const { QuoteService } = await import("@/gen/omnios/v1/quote_connect");
			const qClient = createClient(QuoteService, transport);
			const stream = qClient.processQuote({
				quoteId: id,
				text: processText || "",
				mediaId: mediaId ?? "",
			});
			// Consume stream in background
			(async () => {
				try {
					for await (const _update of stream) {
						// consume
					}
				} catch {
					// errors shown via failed run banner
				}
				qc.invalidateQueries({ queryKey: ["entity-runs", "quote", id] });
				qc.invalidateQueries({ queryKey: COMMERCE_KEYS.quoteReviews(id) });
			})();

			toast.success(t("process", { defaultValue: "Обработка запущена" }));
			setProcessText("");
			setProcessFile(null);
			setActiveTab("positions");
			// Trigger pipeline runs refresh to show processing banner
			qc.invalidateQueries({ queryKey: ["entity-runs", "quote", id] });
		} catch (err) {
			toast.error(
				t("process_error", { defaultValue: "Ошибка обработки документа" }),
			);
		} finally {
			setIsSubmittingProcess(false);
		}
	};

	useWorkspacePage({});

	// ── Loading / Error ──

	if (isLoading) return <DetailSkeleton />;

	if (error || !quote) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-center">
				<p className="text-muted-foreground">
					{t("not_found", { defaultValue: "КП не найдено" })}
				</p>
				<Button
					variant="link"
					onClick={() => navigate({ to: "/commerce/quotes" })}
				>
					{t("back_to_list")}
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6 h-full overflow-auto">
			{/* ── Processing Banner ── */}
			{activeRuns.length > 0 && (
				<Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
					<CardContent className="flex items-center gap-3 p-4 text-sm">
						<Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin shrink-0" />
						<div className="flex-1 min-w-0">
							<p className="font-medium">
								{t("processing_title", {
									defaultValue: "Обработка документа...",
								})}
							</p>
							{activeRuns[0]?.progressMessage && (
								<p className="text-muted-foreground text-xs">
									{activeRuns[0].progressMessage}
								</p>
							)}
						</div>
						{(activeRuns[0]?.progressPct ?? 0) > 0 && (
							<div className="flex items-center gap-2 shrink-0">
								<div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
									<div
										className="h-full rounded-full bg-blue-500 transition-all duration-300"
										style={{
											width: `${activeRuns[0].progressPct}%`,
										}}
									/>
								</div>
								<span className="text-xs tabular-nums text-muted-foreground">
									{activeRuns[0].progressPct}%
								</span>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{/* ── Failed Run Banner ── */}
			{!activeRuns.length && lastFailedRun && (
				<Card className="border-destructive/30 bg-destructive/5">
					<CardContent className="flex items-center gap-3 p-4 text-sm">
						<AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
						<div className="flex-1 min-w-0">
							<p className="font-medium">
								{t("processing_failed", {
									defaultValue: "Ошибка обработки",
								})}
							</p>
							<p className="text-xs text-muted-foreground">
								{lastFailedRun.stoppedAtStep}
							</p>
						</div>
						<Button
							size="sm"
							variant="outline"
							onClick={() => {
								setActiveTab("document");
								setTimeout(() => textareaRef.current?.focus(), 100);
							}}
						>
							{t("retry", { defaultValue: "Повторить" })}
						</Button>
					</CardContent>
				</Card>
			)}

			{/* ── Status Banners ── */}
			{isDraft && isEmpty && (
				<Card className="border-primary/20 bg-primary/5">
					<CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
						<div className="space-y-1">
							<p className="text-sm font-medium">{t("draft_banner_title")}</p>
							<p className="text-sm text-muted-foreground">
								{t("draft_banner_desc")}
							</p>
						</div>
						<Button
							size="sm"
							onClick={() => {
								setActiveTab("document");
								setTimeout(() => textareaRef.current?.focus(), 100);
							}}
						>
							<FileText className="h-4 w-4 mr-1" />
							{t("process")}
						</Button>
					</CardContent>
				</Card>
			)}

			{isNegotiating && (
				<Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
					<CardContent className="flex items-center gap-3 p-4 text-sm">
						<div className="min-w-0">
							<p className="font-medium">{t("status_negotiating")}</p>
							<p className="text-muted-foreground">
								{t("negotiating_banner")}
							</p>
						</div>
					</CardContent>
				</Card>
			)}

			{isAccepted && !isConverted && (
				<Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
					<CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
						<div className="space-y-1">
							<p className="text-sm font-medium">{t("status_accepted")}</p>
							<p className="text-sm text-muted-foreground">
								{t("accepted_banner")}
							</p>
						</div>
						<Button
							size="sm"
							onClick={() =>
								convertMut.mutate(undefined, {
									onSuccess: (resp) => {
										if (resp.orderId) {
											toast.success(t("order_created"));
											navigate({
												to: "/commerce/orders/$id",
												params: { id: resp.orderId },
											});
										}
									},
								})
							}
							disabled={convertMut.isPending}
						>
							{convertMut.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin mr-1" />
							) : (
								<ArrowRight className="h-4 w-4 mr-1" />
							)}
							{t("convert_to_order")}
						</Button>
					</CardContent>
				</Card>
			)}

			{isConverted && quote.orderId && (
				<Card className="border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-950/20">
					<CardContent className="flex items-center gap-3 p-4 text-sm">
						<Check className="h-4 w-4 text-teal-600 dark:text-teal-400" />
						<p className="font-medium">{t("converted_banner")}</p>
						<Link
							to="/commerce/orders/$id"
							params={{ id: quote.orderId }}
							className="ml-auto flex items-center gap-1.5 text-sm text-primary hover:underline"
						>
							<ExternalLink className="h-3.5 w-3.5" />
							{t("open_order")}
						</Link>
					</CardContent>
				</Card>
			)}

			{/* ── Header ── */}
			<div className="flex items-center gap-3">
				<Link
					to="/commerce/quotes"
					className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
					aria-label={t("back_to_list")}
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<CommerceStatusBadge entity="quote" status={quote.status} />
					</div>
					<Input
						value={title}
						onChange={(e) => {
							setTitle(e.target.value);
							setTitleDirty(true);
						}}
						className="mt-1 text-lg font-semibold border-0 px-0 shadow-none focus-visible:ring-0 h-auto"
					/>
				</div>
				{titleDirty && (
					<Button
						size="sm"
						onClick={() =>
							updateMut.mutate(
								{ title },
								{
									onSuccess: () => setTitleDirty(false),
									onError: () =>
										toast.error(t("save_lines", { defaultValue: "Ошибка" })),
								},
							)
						}
						disabled={updateMut.isPending}
					>
						{updateMut.isPending ? (
							<Loader2 className="h-4 w-4 animate-spin mr-1" />
						) : (
							<Save className="h-4 w-4 mr-1" />
						)}
						{t("save_lines", { defaultValue: "Сохранить" })}
					</Button>
				)}
			</div>

			{/* ── Grid: Main + Sidebar ── */}
			<div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
				<div className="lg:col-span-3 space-y-4">
					{/* Summary strip */}
					{hasReviews && (
						<QuoteSummaryStrip
							reviewItems={reviewItems}
							quoteStatus={quote.status}
							onFinalize={() =>
								convertMut.mutate(undefined, {
									onSuccess: (resp) => {
										if (resp.orderId) {
											toast.success(t("order_created"));
											navigate({
												to: "/commerce/orders/$id",
												params: { id: resp.orderId },
											});
										}
									},
								})
							}
							isFinalizing={convertMut.isPending}
						/>
					)}

					{/* Tabs */}
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="space-y-4"
					>
						<TabsList>
							<TabsTrigger value="positions">
								{t("tab_positions")}
								{hasReviews && (
									<span className="ml-1.5 text-xs tabular-nums text-muted-foreground">
										{reviewItems.length}
									</span>
								)}
							</TabsTrigger>
							<TabsTrigger value="document">
								{t("tab_document")}
							</TabsTrigger>
						</TabsList>

						<TabsContent value="positions" className="space-y-4">
							{hasReviews ? (
								<QuoteReviewTable
									items={reviewItems}
									quoteId={id}
								/>
							) : (quote?.lines?.length ?? 0) > 0 ? (
								<QuoteLinesReadonly lines={quote.lines} currency={quote.currency} />
							) : (
								<div className="flex-1 flex items-center justify-center min-h-[300px]">
									<div className="text-center max-w-sm">
										<div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
											<ClipboardPaste className="h-6 w-6 text-muted-foreground" />
										</div>
										<h3 className="text-sm font-medium mb-1">
											{t("empty_positions", { defaultValue: "Нет позиций" })}
										</h3>
										<p className="text-xs text-muted-foreground mb-4">
											{t("process_hint")}
										</p>
										{canEdit && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													setActiveTab("document");
													setTimeout(
														() => textareaRef.current?.focus(),
														100,
													);
												}}
											>
												<FileText className="h-4 w-4 mr-1" />
												{t("process")}
											</Button>
										)}
									</div>
								</div>
							)}
						</TabsContent>

						<TabsContent value="document" className="space-y-4">
							<div className="rounded-lg border bg-muted/30 p-6 space-y-4">
								<div className="flex items-center gap-2">
									<FileText className="h-4 w-4 text-muted-foreground" />
									<span className="text-sm font-medium">{t("process")}</span>
								</div>
								<p className="text-xs text-muted-foreground">
									{t("process_hint")}
								</p>

								{/* File drop zone */}
								<div
									className={`relative rounded-lg border-2 border-dashed transition-colors ${
										dragOver
											? "border-primary bg-primary/5"
											: processFile
												? "border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20"
												: "border-muted-foreground/25 hover:border-muted-foreground/40"
									}`}
									onDragOver={(e) => {
										e.preventDefault();
										setDragOver(true);
									}}
									onDragLeave={() => setDragOver(false)}
									onDrop={(e) => {
										e.preventDefault();
										setDragOver(false);
										const file = e.dataTransfer.files?.[0];
										if (file) setProcessFile(file);
									}}
								>
									<input
										ref={fileInputRef}
										type="file"
										accept={ACCEPTED_FILE_TYPES}
										className="hidden"
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) setProcessFile(file);
										}}
									/>
									{processFile ? (
										<div className="flex items-center gap-3 px-4 py-3">
											<FileUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium truncate">
													{processFile.name}
												</p>
												<p className="text-xs text-muted-foreground">
													{(processFile.size / 1024).toFixed(0)} КБ
												</p>
											</div>
											<Button
												size="icon"
												variant="ghost"
												className="h-7 w-7 shrink-0"
												onClick={() => {
													setProcessFile(null);
													if (fileInputRef.current) fileInputRef.current.value = "";
												}}
											>
												<X className="h-3.5 w-3.5" />
											</Button>
										</div>
									) : (
										<button
											type="button"
											className="flex flex-col items-center gap-2 w-full px-4 py-6 cursor-pointer"
											onClick={() => fileInputRef.current?.click()}
											disabled={!canEdit}
										>
											<FileUp className="h-8 w-8 text-muted-foreground/60" />
											<div className="text-center">
												<p className="text-sm text-muted-foreground">
													{t("drop_file", {
														defaultValue:
															"Перетащите файл или нажмите для выбора",
													})}
												</p>
												<p className="text-xs text-muted-foreground/60 mt-0.5">
													PDF, Excel, Word, CSV, изображения
												</p>
											</div>
										</button>
									)}
								</div>

								{/* Text input */}
								<div>
									<p className="text-xs text-muted-foreground mb-2">
										{t("or_paste_text", {
											defaultValue: "Или вставьте текст:",
										})}
									</p>
									<Textarea
										ref={textareaRef}
										value={processText}
										onChange={(e) => setProcessText(e.target.value)}
										rows={6}
										placeholder={t("process_text_placeholder")}
										className="bg-background resize-y"
										disabled={!canEdit}
									/>
								</div>

								<Button
									size="sm"
									onClick={handleProcess}
									disabled={
										isSubmittingProcess ||
										(!processText.trim() && !processFile) ||
										!canEdit
									}
								>
									{isSubmittingProcess ? (
										<Loader2 className="h-4 w-4 mr-1 animate-spin" />
									) : (
										<FileText className="h-4 w-4 mr-1" />
									)}
									{t("process")}
								</Button>

								{/* Processing history */}
								{allRuns.length > 0 && (
									<div className="border-t pt-4 mt-2">
										<p className="text-xs font-medium text-muted-foreground mb-2">
											{t("processing_history", {
												defaultValue: "История обработки",
											})}
										</p>
										<div className="space-y-1.5">
											{allRuns.map((run) => (
												<div
													key={run.id}
													className="flex items-center gap-2 text-xs"
												>
													{!run.completedAt ? (
														<Loader2 className="h-3 w-3 animate-spin text-blue-500 shrink-0" />
													) : run.stoppedAtStep ? (
														<AlertTriangle className="h-3 w-3 text-destructive shrink-0" />
													) : (
														<Check className="h-3 w-3 text-emerald-500 shrink-0" />
													)}
													<span className="text-muted-foreground">
														{run.pipelineName || run.id.slice(0, 8)}
													</span>
													{run.startedAt && (
														<span className="text-muted-foreground/60 ml-auto">
															{new Date(
																Number(run.startedAt.seconds) * 1000,
															).toLocaleString("ru-RU", {
																day: "numeric",
																month: "short",
																hour: "2-digit",
																minute: "2-digit",
															})}
														</span>
													)}
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</TabsContent>
					</Tabs>
				</div>

				{/* Sidebar */}
				<QuoteSidebar
					quote={quote}
					notes={notes}
					onNotesChange={setNotes}
					onNotesSave={() => updateMut.mutate({ notes })}
					notesDirty={notes !== (quote.notes || "")}
					notesSaving={updateMut.isPending}
					canEdit={canEdit}
					onCustomerChange={(partyId) =>
						updateMut.mutate({ customerPartyId: partyId })
					}
				/>
			</div>

			{/* Counter-offer dialog */}
			<CounterOfferDialog
				quoteId={id}
				open={counterOpen}
				onOpenChange={setCounterOpen}
				initialLines={
					quote.lines.length > 0
						? quote.lines.map((l) => ({
								productId: l.productId,
								quantity: l.quantity,
								unitPriceCents: l.unitPriceCents,
								description: l.description,
								currency: l.currency || quote.currency,
							}))
						: reviewItems
								.filter((r) => {
									const a = r.attrs?.toJson() as Record<string, unknown> | undefined;
									return a?.review_status === "accepted";
								})
								.map((r) => {
									const a = r.attrs?.toJson() as Record<string, unknown>;
									return {
										productId: (a?.matched_product_id as string) ?? "",
										quantity:
											(a?.converted_quantity as number) ??
											(a?.parsed_quantity as number) ??
											1,
										unitPriceCents: BigInt(
											(a?.unit_price_cents as number) ?? 0,
										),
										description: r.title,
										currency: (a?.currency as string) || quote.currency,
									};
								})
				}
			/>
		</div>
	);
}

function QuoteLinesReadonly({
	lines,
}: {
	lines: {
		productId: string;
		productTitle: string;
		quantity: number;
		unitPriceCents: bigint | number | string;
		description: string;
	}[];
	currency: string;
}) {
	const { t } = useTranslation("quotes");
	const totalCents = lines.reduce(
		(sum, l) => sum + Number(l.unitPriceCents) * l.quantity,
		0,
	);

	return (
		<div className="rounded-lg border overflow-hidden">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/30">
						<TableHead className="w-10">#</TableHead>
						<TableHead>{t("description", { defaultValue: "Товар" })}</TableHead>
						<TableHead className="text-right w-20">{t("quantity")}</TableHead>
						<TableHead className="text-right w-28">{t("unit_price")}</TableHead>
						<TableHead className="text-right w-28">{t("line_total")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{lines.map((line, i) => {
						const lineTotal = Number(line.unitPriceCents) * line.quantity;
						return (
							<TableRow key={line.productId + i} className="hover:bg-muted/50">
								<TableCell className="text-muted-foreground tabular-nums">{i + 1}</TableCell>
								<TableCell className="font-medium">
									{line.productTitle || line.description || line.productId}
								</TableCell>
								<TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
								<TableCell className="text-right tabular-nums">
									{formatCurrency(Number(line.unitPriceCents))}
								</TableCell>
								<TableCell className="text-right tabular-nums font-medium">
									{formatCurrency(lineTotal)}
								</TableCell>
							</TableRow>
						);
					})}
					<TableRow>
						<TableCell colSpan={4} className="text-right font-medium">{t("total")}</TableCell>
						<TableCell className="text-right tabular-nums font-semibold">
							{formatCurrency(totalCents)}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</div>
	);
}
